import { useCallback, useEffect, useRef, useState } from 'react'
import { API_STATUS, fetchLoopNetListings } from '../services/marketDataService'

// Minimum unit threshold for multifamily deals (user requirement: 10+ units only)
const MIN_UNITS_MULTIFAMILY = 10

// ── Helpers ──────────────────────────────────────────────────────────────────
function numberFrom(...values) {
  for (const v of values) {
    if (v == null || v === '') continue
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v
    const cleaned = String(v).replace(/[$,%]/g, '').replace(/,/g, '').trim()
    const suffixMatch = cleaned.match(/^([\d.]+)\s*([kmb])$/i)
    if (suffixMatch) {
      const base = Number(suffixMatch[1])
      const multiplier = suffixMatch[2].toLowerCase() === 'k' ? 1_000 : suffixMatch[2].toLowerCase() === 'm' ? 1_000_000 : 1_000_000_000
      if (Number.isFinite(base) && base > 0) return base * multiplier
    }
    const n = Number(cleaned)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

function parseAddress(listing) {
  // LoopNet may use different field names depending on endpoint variant
  const formatted = listing.address || listing.formattedAddress || listing.full_address || ''
  const parts = formatted.split(',').map(p => p.trim()).filter(Boolean)
  return {
    line1: listing.street || listing.addressLine1 || listing.address_line1 || parts[0] || 'Address unavailable',
    city:  listing.city   || parts[1] || '',
    state: listing.state  || listing.stateCode || parts[2]?.split(' ')[0] || '',
    zip:   listing.zip    || listing.zipCode    || listing.postal_code || parts[2]?.match(/\b\d{5}\b/)?.[0] || '',
  }
}

function scoreListing(listing) {
  const price = numberFrom(listing.price, listing.listPrice, listing.asking_price)
  const dom   = numberFrom(listing.daysOnMarket, listing.days_on_market) ?? 30
  const type  = (listing.propertyType || listing.property_type || listing.subTitle || '').toLowerCase()
  let score = 68
  if (isMultifamilyListing(listing)) score += 12
  if (price && price >= 1_000_000 && price <= 8_000_000) score += 8
  if (dom <= 14) score += 8
  else if (dom <= 45) score += 4
  if ((listing.status || '').toLowerCase() === 'active') score += 4
  return Math.max(45, Math.min(94, Math.round(score)))
}

function isMultifamilyListing(listing) {
  const requestedAllDeals = (listing.requestedPropertyType || '').toLowerCase() === 'all'
  if (requestedAllDeals) return true

  const text = [
    listing.propertyType,
    listing.property_type,
    listing.buildingType,
    listing.category,
    listing.subTitle,
    listing.title,
    listing.name,
    listing.address,
  ].filter(Boolean).join(' ').toLowerCase()

  return (
    text.includes('multifamily') ||
    text.includes('multi-family') ||
    text.includes('apartment') ||
    /\b\d+\s*(unit|units|u)\b/i.test(text)
  )
}

function normalizeListing(listing, index) {
  const address  = parseAddress(listing)
  const price    = numberFrom(listing.price, listing.listPrice, listing.asking_price)
  const sqFt     = numberFrom(listing.squareFootage, listing.square_footage, listing.size, listing.livingArea)
  const units    = numberFrom(listing.units, listing.numUnits, listing.num_units)
  const fallback = sqFt ? Math.max(1, Math.round(sqFt / 850)) : 1
  const capRate  = numberFrom(listing.capRate, listing.cap_rate, listing.estimatedCapRate)
  const id       = listing.id || listing.listingId || listing.listing_id || index

  return {
    id:           `LN-${id}`,
    externalId:   String(id),
    name:         listing.name || listing.title || `${address.line1}, ${address.city}` || `LoopNet Listing ${index + 1}`,
    source:       'loopnet_live',
    location:     address.line1,
    city:         address.city,
    state:        address.state,
    zip:          address.zip,
    price,
    units:        units || fallback,
    unitsKnown:   Boolean(units),
    buildingType: listing.propertyType || listing.property_type || listing.type || 'Multifamily',
    yearBuilt:    numberFrom(listing.yearBuilt, listing.year_built) || 'N/A',
    squareFeet:   sqFt,
    capRate:      capRate ? Number(capRate.toFixed(1)) : null,
    score:        scoreListing(listing),
    status:       listing.status || 'Active',
    listedDate:   listing.listedDate || listing.listed_date || listing.createdDate || null,
    daysOnMarket: numberFrom(listing.daysOnMarket, listing.days_on_market),
    sourceUrl:    listing.listingUrl || listing.listing_url || listing.url || null,
    rawListing:   listing,
  }
}

// Map 2-letter state codes → "City, ST" search strings LoopNet understands
const STATE_DEFAULT_CITY = {
  TX: 'Austin, TX',
  FL: 'Miami, FL',
  AZ: 'Phoenix, AZ',
  CA: 'Los Angeles, CA',
  NY: 'New York, NY',
  IL: 'Chicago, IL',
  GA: 'Atlanta, GA',
  CO: 'Denver, CO',
  NC: 'Charlotte, NC',
  TN: 'Nashville, TN',
  WA: 'Seattle, WA',
  MA: 'Boston, MA',
  PA: 'Philadelphia, PA',
  OH: 'Columbus, OH',
}

function resolveLocation({ location, stateCode }) {
  if (location) return location
  if (stateCode && STATE_DEFAULT_CITY[stateCode]) return STATE_DEFAULT_CITY[stateCode]
  if (stateCode) return stateCode
  return 'Chicago, IL'
}

// ── Hook ─────────────────────────────────────────────────────────────────────
/**
 * useLiveDeals — fetch live LoopNet sale listings.
 *
 * Accepts either:
 *   - { location: 'Chicago, IL' }   — explicit location string
 *   - { stateCode: 'TX' }           — 2-letter state code (mapped to a default city)
 *
 * For multifamily propertyType, only deals with 7+ units are returned
 * (per product requirement: multifamily > 6 units only).
 *
 * `error` is now surfaced to callers instead of being suppressed. A non-null
 * error means the API call failed and the UI should show a status banner.
 * `apiStatus` carries additional details (httpStatus, etc.) for the banner.
 */
export default function useLiveDeals({
  location,
  stateCode,
  propertyType = 'multifamily',
  limit        = 500,
} = {}) {
  const [deals, setDeals]                 = useState([])
  const [loading, setLoading]             = useState(Boolean(API_STATUS.hasLoopNetKey))
  const [error, setError]                 = useState(null)
  const [apiStatus, setApiStatus]         = useState(null)   // { httpStatus, isLive }
  const [lastFetchedAt, setLastFetchedAt] = useState(null)
  const fetchCount = useRef(0)

  const resolvedLocation = resolveLocation({ location, stateCode })

  const load = useCallback(async () => {
    if (!API_STATUS.hasLoopNetKey) {
      setDeals([])
      setLoading(false)
      setError('No LoopNet API key configured. Set VITE_LOOPNET_API_KEY in your .env file.')
      setApiStatus({ httpStatus: null, isLive: false })
      return
    }

    const myFetch = ++fetchCount.current
    setLoading(true)
    setError(null)
    setApiStatus(null)

    try {
      const result = await fetchLoopNetListings({ location: resolvedLocation, propertyType, limit })
      if (myFetch !== fetchCount.current) return

      // fetchLoopNetListings now returns { listings, error, httpStatus, isLive }
      const rawListings = result?.listings ?? (Array.isArray(result) ? result : [])
      const fetchError  = result?.error ?? null
      const httpStatus  = result?.httpStatus ?? null
      const isLive      = result?.isLive ?? false

      if (fetchError) {
        // Surface the error — DO NOT suppress it silently
        console.warn('[useLiveDeals] LoopNet API error:', fetchError)
        setError(fetchError)
        setApiStatus({ httpStatus, isLive: false })
        setDeals([])
        return
      }

      const isMultifamily = propertyType === 'multifamily'
      const normalized = rawListings
        .map((listing, index) => ({
          ...normalizeListing(listing, index),
          requestedPropertyType: propertyType,
        }))
        // Filter: multifamily only + minimum 10 units + max 100+ is allowed (no upper cap)
        .filter(d => {
          if (isMultifamily && !isMultifamilyListing(d)) return false
          // Hard floor: exclude listings KNOWN to have fewer than 10 units.
          // Listings without unit data get a display fallback (sqft-derived or 1),
          // which must not trigger exclusion - otherwise sparse API detail
          // responses silently filter out every live listing.
          const u = Number(d.units)
          if (d.unitsKnown && Number.isFinite(u) && u > 0 && u < MIN_UNITS_MULTIFAMILY) return false
          return true
        })

      if (rawListings.length && !normalized.length) {
        console.info('[useLiveDeals] All', rawListings.length, 'live listings were filtered out (propertyType filter). First raw listing:', rawListings[0])
      }

      setDeals(normalized)
      setError(null)
      setApiStatus({ httpStatus: 200, isLive: true })
      setLastFetchedAt(new Date())
    } catch (err) {
      if (myFetch !== fetchCount.current) return
      // Surface ALL errors — no silent suppression
      console.warn('[useLiveDeals] Unexpected error:', err.message)
      setError(`Unexpected error fetching LoopNet data: ${err.message}`)
      setApiStatus({ httpStatus: null, isLive: false })
      setDeals([])
    } finally {
      if (myFetch === fetchCount.current) setLoading(false)
    }
  }, [resolvedLocation, propertyType, limit])

  useEffect(() => { load() }, [load])

  return { deals, loading, error, apiStatus, lastFetchedAt, refresh: load }
}
